/**
 * App state. One module, one object, explicit renders — no framework, no reactivity.
 *
 * The whole app is a linear pipeline over one value: vault config + PSBT bytes in, an `Intent`
 * and a `Finding[]` out. `packages/core` computes both in a single pass, so there is nothing
 * here for a reconciler to do.
 */
import type { CheckResult } from './control.js';
import {
  parseDescriptor, SINGLE_SIG_CLASSES, deriveIntent, runRiskCatalog, payloadHash, intentHash,
  worstSeverity, signingDisabled, requiresAcknowledgement, resolveSigners, isFinalized,
  combine, finalize, extract,
  type ParsedDescriptor, type Network, type Intent, type Finding, type Severity,
  type ControlChallenge, type ScanWindow,
  type Extracted,
} from '@signingroom/core';

export interface VaultConfig {
  descriptor: string;
  /**
   * ⛔ NOT a user setting. Derived from the descriptor and kept only so a stored vault reloads
   * with something sane before parsing. `activeNetwork()` is the authority.
   */
  network: Network;
  /** Highest index the vault has actually used, per branch. Not a guess — see below. */
  lastUsedReceive: number;
  lastUsedChange: number;
  gapLimit: number;
}

export const DEFAULT_VAULT: VaultConfig = {
  descriptor: '',
  network: 'testnet',
  lastUsedReceive: 0,
  lastUsedChange: 0,
  gapLimit: 20,
};

/** A PSBT the user has loaded, with everything derived from it. */
export interface Loaded {
  bytes: Uint8Array;
  intent: Intent;
  findings: Finding[];
  payloadHash: string;
  intentHash: string;
  signers: string[];
  finalized: boolean;
  worst: Severity;
  blocked: boolean;
  mustAcknowledge: Finding[];
}

/** One contribution from a signer, kept separately so the room can be reconstructed. */
export interface Contribution {
  bytes: Uint8Array;
  fingerprints: string[];
  /** Where it came from, for the audit line. */
  label: string;
}

export interface Assembly {
  /** Everything collected so far, including the PSBT originally loaded. */
  contributions: Contribution[];
  /** combine() of every contribution. Recomputed, never patched. */
  combined: Uint8Array | null;
  /** Distinct keys that have signed the combined PSBT. One key, one vote. */
  signers: string[];
  finalized: Uint8Array | null;
  extracted: Extracted | null;
  error: string | null;
}

/**
 * The proof-of-control screen's state.
 *
 * `challenge` is kept as the PARSED object alongside the text it came from, because the message
 * that gets signed is derived from the object — re-parsing the textarea on every keystroke would
 * let the message shown drift from the message the proof commits to while somebody is mid-edit.
 */
export interface ControlState {
  mode: 'prove' | 'check';
  /** Raw text of the challenge, as pasted or as issued here. */
  challengeText: string;
  challenge: ControlChallenge | null;
  /** True when this page generated the nonce, which is a weaker claim — the UI says so. */
  selfIssued: boolean;
  /** The exact bytes to sign, derived from `challenge` and never retyped. */
  message: string;
  /** The address the signature was made at — what the wallet shows, not what it derives. */
  address: string;
  signature: string;
  proofJson: string;
  /** The separate box on the "I was sent a proof" side. */
  checkText: string;
  result: CheckResult | null;
  error: string | null;
}

export function emptyControl(): ControlState {
  return {
    mode: 'prove', challengeText: '', challenge: null, selfIssued: false, message: '',
    address: '', signature: '', proofJson: '', checkText: '', result: null, error: null,
  };
}

export interface QrOut {
  open: boolean;
  format: 'ur' | 'bbqr';
  frames: string[];
  /** Which PSBT is being displayed — the one to sign, or the assembled one. */
  source: 'to sign' | 'combined';
  /** ms per frame. User-adjustable: fixed-focus device cameras need longer than phones. */
  interval: number;
}

export interface LedgerState {
  connected: boolean;
  fingerprint: string | null;
  needsRegistration: boolean;
  message: string | null;
  error: string | null;
}

export interface AppState {
  vault: VaultConfig;
  parsed: ParsedDescriptor | null;
  /** Why the descriptor did not parse, shown verbatim — the error codes are the product. */
  vaultError: string | null;
  loaded: Loaded | null;
  loadError: string | null;
  /** Codes the user has explicitly ticked off. Never persisted: it is per-transaction. */
  acknowledged: Set<string>;
  assembly: Assembly;
  qr: QrOut;
  ledger: LedgerState;
  control: ControlState;
  /** Which screen is showing. Two products in one page: read a payment, or prove a wallet. */
  tab: 'inspect' | 'control';
}

export const emptyAssembly = (): Assembly => ({
  contributions: [], combined: null, signers: [], finalized: null, extracted: null, error: null,
});

export const state: AppState = {
  vault: { ...DEFAULT_VAULT },
  parsed: null,
  vaultError: null,
  loaded: null,
  loadError: null,
  acknowledged: new Set(),
  assembly: emptyAssembly(),
  qr: { open: false, format: 'ur', frames: [], source: 'to sign', interval: 300 },
  ledger: { connected: false, fingerprint: null, needsRegistration: false, message: null, error: null },
  control: emptyControl(),
  // Proof of wallet control is the thesis, so it is what opens. Reading a payment is the second
  // thing this page does, not the first.
  tab: 'control',
};

/* ── persistence ──────────────────────────────────────────────────────────── */

const KEY = 'signingroom.solo.vault.v1';

/**
 * The vault config persists; nothing else does.
 *
 * A descriptor is watch-only and cannot spend, but it does reveal every address the vault will
 * ever use — so this is a real privacy artifact, not a harmless preference. It is stored because
 * retyping a 300-character descriptor before every review is how people end up not checking it,
 * and the UI carries an explicit "Forget this vault" control rather than hiding the storage.
 *
 * Wrapped in try/catch on both sides: private windows, cleared site data and storage-blocking
 * settings all make these throw, and a signing surface that fails to load because localStorage
 * was unavailable would be an absurd way to lose access to the tool.
 */
export function loadVault(): void {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return;
    const v = JSON.parse(stored) as Partial<VaultConfig>;
    state.vault = { ...DEFAULT_VAULT, ...v };
  } catch { /* start with defaults */ }
}

export function saveVault(): void {
  try { localStorage.setItem(KEY, JSON.stringify(state.vault)); } catch { /* ephemeral */ }
}

export function forgetVault(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
  state.vault = { ...DEFAULT_VAULT };
  state.parsed = null;
  state.vaultError = null;
  clearLoaded();
}

/* ── transitions ──────────────────────────────────────────────────────────── */

export function clearLoaded(): void {
  state.loaded = null;
  state.loadError = null;
  state.acknowledged.clear();
  state.assembly = emptyAssembly();
  // The QR panel is derived state too — see `analyse`. Frames encoding a transaction that is no
  // longer on screen are the most dangerous thing this app can display, because a camera does
  // not read the heading above them.
  clearQr();
}

/**
 * Tear down the QR panel's STATE. The DOM side (frame timer, frame index) lives in `main.ts`,
 * which calls this — the split is deliberate, but there is exactly one place that decides
 * `frames` is empty.
 *
 * `format` and `interval` survive on purpose: they are the operator's preferences about their
 * own device, not anything derived from a transaction.
 */
export function clearQr(): void {
  state.qr = {
    open: false, format: state.qr.format, frames: [], source: 'to sign',
    interval: state.qr.interval,
  };
}

/**
 * Ask to re-encode the open QR panel in a different format.
 *
 * Returns the bytes to encode, or null — and when it returns null the panel has ALREADY been
 * torn down, which is the whole point.
 *
 * The old handler set `state.qr.format` first and re-encoded only `if (bytes)`. With no source
 * PSBT — cleared, or replaced by a second document — the assignment landed, the button's
 * `aria-pressed` moved to the new format, and nothing re-encoded: the PREVIOUS format's frames
 * stayed on screen labelled as the other one. A Coldcard pointed at that screen reads BBQr while
 * the page says `ur:crypto-psbt`, and the person holding the camera is the one who cannot see
 * the contradiction. Same failure as the second-document bug in `analyse`, one control along.
 *
 * So the format is not changed until the re-encode is known to be possible.
 */
export function switchQrFormat(next: QrOut['format']): Uint8Array | null {
  const bytes =
    state.qr.source === 'combined' ? state.assembly.combined : state.loaded?.bytes ?? null;
  if (!bytes) { clearQr(); return null; }
  state.qr.format = next;
  return bytes;
}

/** Re-parse the descriptor. Any change invalidates a loaded PSBT — see `applyVault`. */
export function parseVault(): void {
  state.parsed = null;
  state.vaultError = null;
  const d = state.vault.descriptor.trim();
  if (!d) return;
  try {
    /*
     * SOLO IS THE SINGLE-SIGNATURE PRODUCT. A shared wallet is refused here, by design.
     *
     * Not a limitation of the engine — `packages/core` reads both — but a product boundary:
     * a wallet several people must approve needs somewhere for them to do it, which is Org.
     * Passing the narrowed set makes that boundary a fact about this build rather than a rule
     * somebody has to remember, and `diagnoseDescriptor` turns the refusal into a sentence that
     * names the other product instead of an error code.
     */
    state.parsed = parseDescriptor(d, SINGLE_SIG_CLASSES);
    // Keep the stored value in step so a reload starts consistent.
    state.vault.network = state.parsed.network;
  } catch (e) {
    state.vaultError = (e as Error).message;
  }
}

/**
 * Apply a vault change and re-derive.
 *
 * Changing the scan window changes what `isMine` answers, which changes whether a change output
 * is CHANGE_NOT_MINE. Keeping a previously-computed verdict on screen after the inputs moved is
 * how a stale green tick outlives the thing that justified it, so the findings are always
 * recomputed from the current config — never patched.
 */
export function applyVault(): void {
  parseVault();
  saveVault();
  if (state.loaded) {
    // `analyse` clears everything derived from the previous PSBT itself now, so there is one
    // place that owns the reset rather than two that have to agree.
    analyse(state.loaded.bytes);
  }
}

/**
 * Derive everything from PSBT bytes. Throws nothing — failures land in `loadError`.
 *
 * ⚠️ EVERYTHING DERIVED FROM THE PREVIOUS PSBT IS DISCARDED FIRST, not just the findings.
 *
 * This used to reset `loadError` and `acknowledged` and stop there, while `assembly` and `qr`
 * were cleared only by the Clear button and by `applyVault`. Loading a second PSBT through the
 * Inspect path therefore left the FIRST transaction's signatures, finalized bytes, txid and QR
 * frames on screen underneath the second one's amounts. An audit reproduced it on the live site:
 * the panel read "sending 1.23456789 BTC" from PSBT B while the assemble track read "2 of 2
 * required signatures … policy met" and the export buttons handed over transaction A.
 *
 * Three separate lies fell out of one omission. The flow bar — which is documented as "derived
 * from state, never set by a click" — marked Sign, Combine and Finalize done for a transaction
 * with zero signatures. "Copy raw hex" and "Download final .psbt" served A under a heading
 * describing B. And the animated QR kept showing A's frames labelled "to sign", so a user who
 * corrected a PSBT with the panel open and held a Coldcard to the screen would scan the OLD
 * transaction.
 *
 * That last one is the precise failure this product exists to prevent: what you read is not what
 * you sign. The module docstring's claim that "there is no path where a finding from a previous
 * PSBT survives next to a fresh intent panel" was true only of `findings`.
 */
/**
 * The scan window, from the wallet settings. ONE builder, because the inspector and the
 * proof-of-control screen must look through exactly the same addresses — if they differed, an
 * address the inspector calls yours could be one the proof screen cannot find.
 */
export function scanWindow(): ScanWindow {
  const { gapLimit, lastUsedReceive, lastUsedChange } = state.vault;
  return { lastUsed: { 0: lastUsedReceive, 1: lastUsedChange }, gapLimit };
}

export function analyse(bytes: Uint8Array): void {
  clearLoaded();
  if (!state.parsed) {
    state.loaded = null;
    state.loadError = 'Set up the vault descriptor first — ownership cannot be checked without it.';
    return;
  }
  const network = activeNetwork();
  try {
    const window = scanWindow();
    const intent = deriveIntent(bytes, { descriptor: state.parsed, window, network });
    // No chain data: Solo cannot reach the network by construction. The checks that need it
    // degrade to UNVERIFIABLE inside the catalog rather than quietly passing.
    const findings = runRiskCatalog(intent, { descriptor: state.parsed, network }, {});
    state.loaded = {
      bytes,
      intent,
      findings,
      payloadHash: payloadHash(bytes),
      intentHash: intentHash(intent),
      // Descriptor-resolved: Core strips bip32Derivation when it signs, so asking the PSBT
      // who signed comes back empty on anything Core produced.
      signers: resolveSigners(bytes, state.parsed, window),
      finalized: isFinalized(bytes),
      worst: worstSeverity(findings),
      blocked: signingDisabled(findings),
      mustAcknowledge: requiresAcknowledgement(findings),
    };
  } catch (e) {
    state.loaded = null;
    state.loadError = (e as Error).message;
  }
}

/**
 * The network is a PROPERTY OF THE DESCRIPTOR, not a preference.
 *
 * A tpub descriptor is testnet; there is nothing to choose. Offering a dropdown let the two
 * disagree, and the app then rendered `bc1q…` mainnet addresses for a testnet transaction while
 * raising no finding — because it passed the same user-chosen value into both sides of the
 * NETWORK_MISMATCH comparison, so the check compared a value against itself.
 *
 * The dangerous direction is the inverse of what testing happened to hit: a mainnet spend shown
 * as `tb1q…` invites someone to wave it through as "just a test".
 */
export const activeNetwork = (): Network => state.parsed?.network ?? state.vault.network;

/** Every acknowledgement ticked? Governs whether the user may proceed. */
export const allAcknowledged = (): boolean =>
  !!state.loaded && state.loaded.mustAcknowledge.every((f) => state.acknowledged.has(f.code));


/* ── assembly (BIP-174 roles 4, 5, 6) ─────────────────────────────────────── */

/**
 * Combining, finalizing and extracting are MECHANICAL. They merge signatures and assemble
 * witnesses; they cannot change what gets paid. The UI labels them as system steps for exactly
 * that reason — so nobody mistakes assembly for another approval.
 *
 * What is NOT mechanical is accepting bytes back from a signer. A signer who returns a PSBT for
 * a *different* transaction must be caught here and named, not quietly merged. `combine()` in
 * core already refuses a mismatched unsigned tx, but its error is about serialisation; this
 * checks the property we actually care about — same intent, same hash — and says so in those
 * terms.
 */
export function addContribution(bytes: Uint8Array, label: string): void {
  const A = state.assembly;
  A.error = null;
  if (!state.loaded) { A.error = 'Inspect a transaction before collecting signatures.'; return; }

  try {
    const intent = deriveIntent(bytes, {
      descriptor: state.parsed!,
      window: { lastUsed: { 0: state.vault.lastUsedReceive, 1: state.vault.lastUsedChange },
                gapLimit: state.vault.gapLimit },
      network: activeNetwork(),
    });
    if (intentHash(intent) !== state.loaded.intentHash) {
      A.error = `SIGNER_RETURNED_DIFFERENT_TX: this PSBT does not describe the transaction you ` +
        `reviewed. Its intent hash is ${intentHash(intent).slice(0, 16)}… but you approved ` +
        `${state.loaded.intentHash.slice(0, 16)}…. Do not combine it — ask the signer what they signed.`;
      return;
    }
  } catch (e) {
    A.error = `Could not read that as a PSBT for this vault: ${(e as Error).message}`;
    return;
  }

  // A finalized PSBT is not "unsigned" — it is finished. Its signatures have moved out of
  // `partialSig` and into the final witness, so counting partial signatures reports zero and the
  // honest-looking message "carries no signatures" is exactly backwards. Say what happened.
  if (isFinalized(bytes)) {
    A.error = 'That PSBT is already FINALIZED — every input carries a complete witness, so there ' +
      'is nothing left to sign or combine. Load it in step 2 to inspect it, or export the ' +
      'transaction from there.';
    return;
  }

  const fps = resolveSigners(bytes, state.parsed ?? undefined, {
    lastUsed: { 0: state.vault.lastUsedReceive, 1: state.vault.lastUsedChange },
    gapLimit: state.vault.gapLimit });
  if (fps.length === 0) {
    A.error = 'That PSBT carries no signatures for this vault — nothing to add. Check it was ' +
      'signed by a key that belongs to this descriptor.';
    return;
  }

  const known = new Set(A.contributions.flatMap((c) => c.fingerprints));
  if (fps.every((f) => known.has(f))) {
    A.error = `Already have a signature from ${fps.join(', ')}. One key, one vote.`;
    return;
  }

  A.contributions.push({ bytes, fingerprints: fps, label });
  recombine();
}

/** Recompute from the contribution set. Never patches a previous result. */
export function recombine(): void {
  const A = state.assembly;
  A.combined = null; A.signers = []; A.finalized = null; A.extracted = null;
  if (A.contributions.length === 0) return;
  try {
    A.combined = combine(A.contributions.map((c) => c.bytes));
    A.signers = resolveSigners(A.combined, state.parsed ?? undefined, {
      lastUsed: { 0: state.vault.lastUsedReceive, 1: state.vault.lastUsedChange },
      gapLimit: state.vault.gapLimit });
    if (isFinalized(A.combined)) {
      A.finalized = A.combined;
      A.extracted = extract(A.combined);
    }
  } catch (e) {
    A.error = (e as Error).message;
  }
}

/** Assemble witnesses, then produce the broadcastable transaction. */
export function finalizeNow(): void {
  const A = state.assembly;
  A.error = null;
  if (!A.combined) { A.error = 'Nothing to finalize.'; return; }
  try {
    A.finalized = finalize(A.combined);
    A.extracted = extract(A.finalized);
  } catch (e) {
    A.finalized = null; A.extracted = null;
    A.error = (e as Error).message;
  }
}

/** m of n satisfied? Derived from the descriptor, never assumed. */
export const policyMet = (): boolean =>
  !!state.parsed && state.assembly.signers.length >= state.parsed.m;


/* ── step-panel collapse ──────────────────────────────────────────────────── */

export interface PanelSettled { vault: boolean; load: boolean }

/**
 * Which step panels should keep their OPEN state across a re-render.
 *
 * A panel collapses exactly once — on the render where it becomes settled — and after that the
 * user's own choice wins. Extracted from `render()` and made pure because neither half of the
 * rule works alone and I got it wrong in both directions:
 *
 *   · collapse every render → the panel shuts under somebody typing in the scan window, which
 *     is precisely what a person does after a CHANGE_NOT_MINE;
 *   · preserve every render → it never collapses at all, because the panel was open right up
 *     to the moment it settled.
 *
 * So: preserve, EXCEPT on the transition into settled.
 */
export function panelsToPreserve(
  now: PanelSettled, before: PanelSettled, isOpen: (k: keyof PanelSettled) => boolean,
): (keyof PanelSettled)[] {
  return (['vault', 'load'] as const)
    .filter((k) => !(now[k] && !before[k]) && isOpen(k));
}
